import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AuthScreen } from '@/auth/AuthScreen';
import { PasswordRequirements } from '@/auth/PasswordRequirements';
import { authErrorMessage } from '@/auth/util_authErrors';
import { checkPassword, MIN_PASSWORD_LENGTH } from '@/auth/util_password';
import { auth } from '@/firebaseInit';

/** The two link types Firebase can send us. See the note on the component. */
type ActionModeType = 'resetPassword' | 'verifyEmail';

type StageType = 'checking' | 'ready' | 'done' | 'failed';

const inputClass =
  'tw:w-full tw:rounded tw:border tw:border-white/20 tw:bg-white/5 tw:px-3 tw:py-2.5 tw:text-sm tw:text-white tw:placeholder-white/30 tw:outline-none tw:focus:border-ed-gold tw:disabled:opacity-60';

/**
 * Where to send someone afterwards. Firebase appends `continueUrl` from the
 * `actionCodeSettings` we passed when sending the email.
 *
 * Same-origin only. Firebase already restricts the value to the project's
 * authorised domains, so this is belt and braces — but the parameter arrives in
 * a URL anybody can edit, and a link that carries a stranger's destination is
 * not one we should be following on their behalf.
 */
function safeContinueUrl(raw: string | null): string {
  if (!raw) return '/';
  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}` : '/';
  } catch {
    return '/';
  }
}

/**
 * Handles the links Firebase emails out: `/auth/action?mode=…&oobCode=…`.
 *
 * Firebase hosts a page for this already. It works, and it looks like nothing —
 * generic chrome on a `firebaseapp.com` domain, in the middle of a flow that is
 * otherwise entirely ours. This replaces it, and is what the project's
 * "Customize action URL" setting must point at (see the note in README terms —
 * one console change per project, staging and prod separately; until it is made,
 * Firebase keeps using its own page and this route is simply unvisited).
 *
 * Two modes are handled. `resetPassword` is the one in use. `verifyEmail` costs
 * three lines and is a checkbox away in the console, and a handler that met it
 * with a blank page would be a worse trap than not having one.
 *
 * `recoverEmail` — the "undo an email change" link — is deliberately not here.
 * Nothing in this app changes an account's address, so Firebase has no occasion
 * to send one; if that ever becomes false, this is where it belongs.
 *
 * Mounted outside the AuthGate (see App.tsx): the whole point is to be usable by
 * someone who cannot sign in.
 */
export function PageAuthAction() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') ?? '';
  const oobCode = searchParams.get('oobCode') ?? '';
  const continueTo = safeContinueUrl(searchParams.get('continueUrl'));

  // Decided during render, not in an effect: neither parameter changes while
  // this page is mounted, and a link missing either is dead on arrival.
  const unsupported = !oobCode || (mode !== 'resetPassword' && mode !== 'verifyEmail');

  const [stage, setStage] = useState<StageType>(unsupported ? 'failed' : 'checking');
  const [error, setError] = useState<string | null>(
    unsupported ? 'This link is not one we recognise. Please request a new email.' : null,
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const checks = checkPassword(password, confirmPassword);

  // Check the code before showing anything. For a reset this also tells us whose
  // account it is, which is worth showing — a stale link from an old address is
  // otherwise indistinguishable from a good one until the password fails.
  useEffect(() => {
    if (unsupported) return;
    let cancelled = false;
    const action =
      mode === 'resetPassword'
        ? verifyPasswordResetCode(auth, oobCode).then((address) => {
            if (cancelled) return;
            setEmail(address);
            setStage('ready');
          })
        : applyActionCode(auth, oobCode).then(() => {
            if (!cancelled) setStage('done');
          });

    action.catch((e) => {
      if (cancelled) return;
      console.error('Auth action failed', e);
      setError(authErrorMessage(e));
      setStage('failed');
    });

    return () => {
      cancelled = true;
    };
  }, [unsupported, mode, oobCode]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStage('done');
    } catch (err) {
      console.error('confirmPasswordReset failed', err);
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const actionMode = mode as ActionModeType;

  if (stage === 'checking') {
    return (
      <AuthScreen>
        <Loader2 className='tw:size-8 tw:animate-spin tw:text-ed-gold' />
      </AuthScreen>
    );
  }

  if (stage === 'failed') {
    return (
      <AuthScreen>
        <div className='tw:max-w-md'>
          <h2 className='tw:font-serif tw:text-xl tw:text-white'>Link unavailable</h2>
          <p className='tw:mt-3 tw:text-sm tw:text-white/60'>{error}</p>
          <Link
            to='/'
            className='tw:mt-8 tw:inline-block tw:rounded tw:bg-ed-gold tw:px-8 tw:py-3 tw:text-sm tw:font-medium tw:tracking-wide tw:text-ed-black tw:transition-opacity tw:hover:opacity-90'
          >
            Back to sign in
          </Link>
        </div>
      </AuthScreen>
    );
  }

  if (stage === 'done') {
    return (
      <AuthScreen>
        <div className='tw:max-w-md'>
          <div className='tw:mx-auto tw:mb-6 tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:bg-ed-gold/20'>
            <svg className='tw:size-8 tw:text-ed-gold' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
            </svg>
          </div>
          <h2 className='tw:font-serif tw:text-2xl tw:text-white'>
            {actionMode === 'resetPassword' ? 'Password updated' : 'Email confirmed'}
          </h2>
          <p className='tw:mt-3 tw:text-sm tw:text-white/60'>
            {actionMode === 'resetPassword'
              ? 'Sign in with your new password to continue.'
              : 'Your email address has been verified.'}
          </p>
          {/* Not signed in for them, though we hold a working password by this
              point: a reset is exactly the thing someone does on a borrowed
              computer, and leaving a live session behind would be our doing. */}
          <Link
            to={continueTo}
            className='tw:mt-8 tw:inline-block tw:rounded tw:bg-ed-gold tw:px-8 tw:py-3 tw:text-sm tw:font-medium tw:tracking-wide tw:text-ed-black tw:transition-opacity tw:hover:opacity-90'
          >
            Continue to sign in
          </Link>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className='tw:w-full tw:max-w-sm'>
        <h2 className='tw:font-serif tw:text-2xl tw:text-white'>Choose a new password</h2>
        <p className='tw:mt-3 tw:text-sm tw:text-white/60'>
          for <span className='tw:text-white'>{email}</span>
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className='tw:mt-8 tw:space-y-3 tw:text-left'>
          {/* Present and hidden, not omitted: password managers look for the
              account this credential belongs to, and without it they offer to
              save the new password under no username at all. */}
          <input type='email' value={email} readOnly hidden autoComplete='username' />

          <input
            type='password'
            value={password}
            required
            minLength={MIN_PASSWORD_LENGTH}
            disabled={busy}
            placeholder={`Min ${MIN_PASSWORD_LENGTH} characters`}
            autoComplete='new-password'
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />

          <input
            type='password'
            value={confirmPassword}
            required
            disabled={busy}
            placeholder='Re-enter password'
            autoComplete='new-password'
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
          />

          <PasswordRequirements checks={checks} />

          <button
            type='submit'
            disabled={busy || !checks.ok}
            aria-busy={busy || undefined}
            className='tw:inline-flex tw:w-full tw:items-center tw:justify-center tw:gap-2.5 tw:rounded tw:bg-ed-gold tw:px-6 tw:py-2.5 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-opacity tw:hover:opacity-90 tw:disabled:opacity-60'
          >
            {busy && <Loader2 className='tw:size-4 tw:animate-spin' />}
            {busy ? 'Please wait…' : 'Set new password'}
          </button>

          {error && <p className='tw:text-sm tw:text-ed-red'>{error}</p>}
        </form>
      </div>
    </AuthScreen>
  );
}
